/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { useEffect, useState } from 'preact/compat';

import {
  EstimateAddressInput,
  getCountries,
  getEstimateShipping,
  getRegions,
} from '@/cart/api';
import { PriceProps } from '@adobe-commerce/elsie/components';

export const useEstimatedShipping = () => {
  const [priceSummaryLoading, setPriceSummaryLoading] =
    useState<boolean>(false);

  /* State data related to estimating costs in the Price Summary component */
  const [countries, setCountries] = useState<{ text: string; value: string }[]>(
    []
  );
  const [selectedCountry, setSelectedCountry] = useState<string>('US');
  const [selectedRegion, setSelectedRegion] = useState<string>('');
  const [selectedZip, setSelectedZip] = useState<string>('');
  const [regions, setRegions] = useState<{ text: string; value: string }[]>([]);
  const [regionsLoading, setRegionsLoading] = useState<boolean>(false);
  const [estimatedShippingPrice, setEstimatedShippingPrice] = useState<
    PriceProps | null | any
  >();
  const [estimatedShippingMethod, setEstimatedShippingMethod] = useState<any>();
  const [estimatedDestinationText, setEstimatedDestinationText] =
    useState<string>('');
  const [shippingEstimated, setShippingEstimated] = useState<boolean>(false);

  const resetValues = () => {
    setSelectedCountry('US');
    setSelectedRegion('');
    setSelectedZip('');
    setEstimatedShippingPrice(null);
    setEstimatedShippingMethod(null);
    setEstimatedDestinationText('');
    setShippingEstimated(false);
  };

  const handleEstimateShipping = async (formValues: {
    shippingCountry: string;
    shippingState?: string;
    shippingZip?: string;
  }) => {
    const {
      shippingCountry,
      shippingState = '',
      shippingZip = '',
    } = formValues;

    const addressData: EstimateAddressInput = {
      countryCode: shippingCountry,
      postcode: shippingZip,
      region: {
        region: shippingState,
      },
    };

    setPriceSummaryLoading(true);

    return getEstimateShipping(addressData)
      .then((data) => {
        if (data) {
          setEstimatedShippingPrice({
            amount: data.amount.value,
            currency: data.amount.currency,
            priceIncludingtax: {
              amount: data.price_incl_tax.value,
              currency: data.price_incl_tax.currency,
            },
            priceExcludingtax: {
              amount: data.price_excl_tax.value,
              currency: data.price_excl_tax.currency,
            },
          });
          setEstimatedShippingMethod({
            carrier_code: data.carrier_code,
            method_code: data.method_code,
          });

          setSelectedCountry(shippingCountry);
          setSelectedRegion(shippingState);
          setSelectedZip(shippingZip);

          setEstimatedDestinationText(
            shippingState || shippingZip || shippingCountry
          );

          setShippingEstimated(true);
        }
        setSelectedCountry(shippingCountry);
        setSelectedRegion(shippingState);
        setSelectedZip(shippingZip);

        setEstimatedDestinationText(
          shippingState || shippingZip || shippingCountry
        );
        return data;
      })
      .finally(() => {
        setPriceSummaryLoading(false);
      });
  };

  const handleCountrySelected = (event: Event) => {
    event.preventDefault();

    setSelectedRegion('');
    setSelectedZip('');

    const value = (event.target as HTMLSelectElement).value;
    setSelectedCountry(value);
  };

  // Get list of countries on initial load
  useEffect(() => {
    getCountries().then((countries) => {
      let defaultCountry = 'US';
      const transformedCountries = countries.map((country) => {
        if (country.isDefaultCountry) {
          defaultCountry = country.id;
        }
        return {
          text: country.label,
          value: country.id,
        };
      });
      setCountries(transformedCountries);
      setSelectedCountry(defaultCountry);
    });
  }, []);

  // Update regions when country is updated
  useEffect(() => {
    setRegionsLoading(true);
    getRegions(selectedCountry)
      .then((regions) => {
        const transformedRegions = regions.map((region) => {
          return {
            text: region.name,
            value: region.code,
          };
        });
        setRegions(transformedRegions);
      })
      .finally(() => {
        setRegionsLoading(false);
      });
  }, [selectedCountry, setRegionsLoading]);

  return {
    loading: priceSummaryLoading,
    regionsLoading,
    estimatedDestinationText,
    countries,
    selectedCountry,
    selectedRegion,
    selectedZip,
    regions,
    estimatedShippingPrice,
    estimatedShippingMethod,
    shippingEstimated,
    handleEstimateShipping,
    handleCountrySelected,
    resetValues,
    setPriceSummaryLoading,
  };
};
